import Route from "route-parser";
/**
 * URL generator helper
 * Replace route path variables with provided parameters:
 * - generateUrl("/:id", { id: 1 }) --> "/1"
 * - generateUrl("https://domain.tld/:locale/whatever", { locale: 'se' }) --> "https://domain.tld/se/whatever"
 *
 *
 * @return string
 * @param path
 * @param parameters
 */
export const generateUrl = (path, parameters = {}) => Object.keys(parameters).reduce((url, parameterName) => url.replace(new RegExp(`:${parameterName}`, "gi"), parameters[parameterName]), path);
/**
 * Generate a URL based on a DomainApplication
 * Replace route path variables with provided parameters:
 * - generateApplicationUrl({ host: 'domain.tld' }, '/:locale/whatever', { locale: 'se'}) --> "https://domain.tld/se/whatever"
 *
 * @param application
 * @param path
 * @param parameters
 */
export const generateApplicationUrl = (application, path, parameters = {}) => {
    const baseUrl = new URL(path, `https://${application.host}`);
    return generateUrl(baseUrl.href, parameters);
};
/**
 * Get application by url
 *
 * @param applications
 * @param url
 * @param checkHost
 * @return {DomainApplication | null} The application
 */
export const getApplicationByUrl = (applications, url, checkHost = true) => {
    if (!applications.length)
        return null;
    const application = applications.find((application) => {
        if (checkHost && application.host !== url.host) {
            return false;
        }
        return application.patterns.some((pattern) => {
            const route = new Route(pattern);
            return !!route.match(url.pathname);
        });
    });
    if (!application) {
        return null;
    }
    return application;
};
/**
 * Get application id by url
 *
 * @param applications
 * @param url
 * @param {boolean} checkHost
 * @return {string|null} The application id
 */
export const getApplicationNameByUrl = (applications, url, checkHost = true) => {
    const application = getApplicationByUrl(applications, url, checkHost);
    return application ? application.name : null;
};
/**
 * Check if a url is internal or external
 * @param applications
 * @param {string} targetUrl The url target
 * @param {string} currentUrl The current url, will fallback to window.location.href
 * @param queryStatus
 * @param checkHost
 * @return {boolean}
 */
export const isExternalUrl = (applications, targetUrl, currentUrl, queryStatus, checkHost = true) => {
    currentUrl = currentUrl || new URL(window.location.href);
    // Check query status if available
    // Return true by default so urls are treated as external links
    if (queryStatus && queryStatus.error) {
        console.error(queryStatus.error);
        return true;
    }
    else if (queryStatus && queryStatus.loading) {
        console.warn("Application are not yet loaded.");
        return true;
    }
    if (targetUrl.host !== currentUrl.host) {
        return true;
    }
    return (getApplicationNameByUrl(applications, targetUrl, checkHost) !==
        getApplicationNameByUrl(applications, currentUrl, checkHost));
};
/**
 * Create a URL with a leading and trailing slash
 * @param {...string} parts The url parts
 * @return {string} URL
 */
export const makeURL = (...parts) => {
    parts = parts
        .map((part) => {
        if (!part)
            return;
        if (part[0] === "/")
            part = part.substr(1); // Remove leading slash from argument
        if (part[part.length - 1] === "/")
            part = part.slice(0, -1); // Remove trailing slash from argument
        return part;
    })
        .filter(Boolean); // Remove undefined parts
    const joined = parts.join("/") + "/"; // Join all parts with a slash and add a trailing slash
    const isAbsoluteURL = joined.indexOf("http") === 0;
    return isAbsoluteURL ? joined : `/${joined}`; // Optionally add a leading slash if the url is relative
};
/**
 * Removes non-existing locales from a URL, used before redirect in MarketSelector
 * @param {string} locale correct locale
 * @param {string} path website path that might contain incorrect locale
 * @returns {string}
 */
export const replaceMarketInUrl = (locale, path) => {
    if (path.length) {
        // replace '/xx/' with '/' and '/xx-xx/ with '/'
        path = path.replace(/^\/(\w\w(?:-\w\w)?)(?:\/|$)/, "");
    }
    return makeURL(locale, path);
};
